import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CatalogueApiService } from '@angular-workspace/services';
import { debounceTime, distinctUntilChanged, from, switchMap } from 'rxjs';
import type { NgsildBasicInfo } from '../ngsild.models';

export type TitleAvailabilityStatus =
  | 'checking'
  | 'available'
  | 'not-available'
  | null;

type PermissionValue = 'Open' | 'Restricted' | 'Private';

const SHORT_DESCRIPTION_MIN = 50;
const SHORT_DESCRIPTION_MAX = 200;
const LONG_DESCRIPTION_MIN = 50;
const LONG_DESCRIPTION_MAX = 2000;

@Component({
  selector: 'lib-ngsild-basic-info',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './basic-info.component.html',
  styleUrl: './basic-info.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'ngsild-widget-host',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasicInfoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly validChange = output<boolean>();
  readonly valueChange = output<NgsildBasicInfo>();

  readonly titlePlaceholder = input('Enter dataset title');
  /** When set (e.g. `['Model']`), title availability uses scoped catalogue search. */
  readonly titleCheckAssetTypes = input<string[] | null>(null);

  readonly isPermissionDropdownOpen = signal(false);
  readonly titleAvailabilityStatus = signal<TitleAvailabilityStatus>(null);
  readonly isCheckingTitle = signal(false);

  /** UI labels match legacy Add Dataset; form values stay Open | Restricted | Private for API. */
  readonly permissionOptions: Array<{ value: PermissionValue; label: string }> = [
    { value: 'Open', label: 'Public' },
    { value: 'Restricted', label: 'Restricted' },
    { value: 'Private', label: 'Privacy Computation' },
  ];

  readonly form = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(200)]],
    permission: ['' as '' | PermissionValue, Validators.required],
    shortDescription: [
      '',
      [
        Validators.required,
        Validators.minLength(SHORT_DESCRIPTION_MIN),
        Validators.maxLength(SHORT_DESCRIPTION_MAX),
      ],
    ],
    longDescription: [
      '',
      [
        Validators.required,
        Validators.minLength(LONG_DESCRIPTION_MIN),
        Validators.maxLength(LONG_DESCRIPTION_MAX),
      ],
    ],
  });

  ngOnInit(): void {
    this.emitState();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emitState();
        this.cdr.markForCheck();
      });
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emitState();
        this.cdr.markForCheck();
      });

    this.form.controls.title.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((title) => {
          const trimmed = (title ?? '').trim();
          if (!trimmed) {
            this.isCheckingTitle.set(false);
            this.titleAvailabilityStatus.set(null);
            this.emitState();
            return from(Promise.resolve(true));
          }
          this.isCheckingTitle.set(true);
          this.titleAvailabilityStatus.set('checking');
          this.emitState();
          this.cdr.markForCheck();
          return from(this.checkTitleAvailable(trimmed));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (isAvailable) => {
          this.isCheckingTitle.set(false);
          this.titleAvailabilityStatus.set(
            isAvailable ? 'available' : 'not-available',
          );
          this.emitState();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error checking title availability:', error);
          this.isCheckingTitle.set(false);
          this.titleAvailabilityStatus.set(null);
          this.emitState();
          this.cdr.markForCheck();
        },
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const permissionWrapper = this.elementRef.nativeElement.querySelector('.custom-dropdown-wrapper');
    if (!permissionWrapper || !permissionWrapper.contains(event.target as Node)) {
      if (this.isPermissionDropdownOpen()) {
        this.isPermissionDropdownOpen.set(false);
        this.cdr.markForCheck();
      }
    }
  }

  togglePermissionDropdown(): void {
    this.isPermissionDropdownOpen.update((open) => !open);
  }

  selectPermission(option: { value: PermissionValue; label: string }): void {
    this.form.patchValue({ permission: option.value });
    this.isPermissionDropdownOpen.set(false);
    this.form.controls.permission.markAsTouched();
    this.emitState();
  }

  getSelectedPermissionLabel(): string {
    const selectedValue = this.form.controls.permission.value;
    if (!selectedValue) {
      return 'Select permission';
    }
    const selectedOption = this.permissionOptions.find(
      (opt) => opt.value === selectedValue,
    );
    return selectedOption ? selectedOption.label : 'Select permission';
  }

  isPermissionSelected(value: PermissionValue): boolean {
    return this.form.controls.permission.value === value;
  }

  clearPermission(event: Event): void {
    event.stopPropagation();
    this.form.patchValue({ permission: '' });
    this.form.controls.permission.markAsTouched();
    this.emitState();
  }

  /** Prefill from JSON upload / preview step. */
  applyValue(value: NgsildBasicInfo): void {
    const raw = (value.permission ?? '').trim();
    const permission: PermissionValue =
      raw === 'Restricted' || raw.toUpperCase() === 'RESTRICTED'
        ? 'Restricted'
        : raw === 'Private' ||
            raw === 'Privacy Computation' ||
            raw.toUpperCase() === 'PRIVATE'
          ? 'Private'
          : 'Open';
    this.form.patchValue({
      title: value.title ?? '',
      permission,
      shortDescription: value.shortDescription ?? '',
      longDescription: value.longDescription ?? '',
    });
    const trimmed = (value.title ?? '').trim();
    if (trimmed) {
      this.isCheckingTitle.set(true);
      this.titleAvailabilityStatus.set('checking');
      this.cdr.markForCheck();
      void this.checkTitleAvailable(trimmed).then(
        (isAvailable) => {
          this.isCheckingTitle.set(false);
          this.titleAvailabilityStatus.set(
            isAvailable ? 'available' : 'not-available',
          );
          this.emitState();
          this.cdr.markForCheck();
        },
        (error) => {
          console.error('Error checking title availability:', error);
          this.isCheckingTitle.set(false);
          this.titleAvailabilityStatus.set(null);
          this.emitState();
          this.cdr.markForCheck();
        },
      );
    } else {
      this.emitState();
    }
    this.cdr.markForCheck();
  }

  /** Reset to empty defaults after successful submit. */
  resetForm(): void {
    this.form.reset({
      title: '',
      permission: '',
      shortDescription: '',
      longDescription: '',
    });
    this.isPermissionDropdownOpen.set(false);
    this.isCheckingTitle.set(false);
    this.titleAvailabilityStatus.set(null);
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.emitState();
    this.cdr.markForCheck();
  }

  hasTitleValue(): boolean {
    return this.form.controls.title.value.trim().length > 0;
  }

  isTitleUnavailable(): boolean {
    return this.titleAvailabilityStatus() === 'not-available';
  }

  private async checkTitleAvailable(title: string): Promise<boolean> {
    const assetTypes = this.titleCheckAssetTypes();
    if (!assetTypes?.length) {
      return this.catalogueApi.isItemNameAvailable(title);
    }

    const response = await this.catalogueApi.searchAllAssets(
      '',
      assetTypes,
      [],
      1,
      10,
      'name:asc;itemCreatedAt:asc;',
      [{ searchType: 'term', field: 'name', values: [title] }],
    );

    let items: Array<Record<string, unknown>> = [];
    if (Array.isArray(response)) {
      items = response;
    } else if (response?.result && Array.isArray(response.result)) {
      items = response.result;
    } else if (response?.data && Array.isArray(response.data)) {
      items = response.data;
    } else if (response?.items && Array.isArray(response.items)) {
      items = response.items;
    }

    const titleLower = title.toLowerCase();
    const hasMatch = items.some((item) => {
      const itemName = String(item['name'] ?? item['label'] ?? '').toLowerCase();
      return itemName === titleLower;
    });
    return !hasMatch;
  }

  private isFormReady(): boolean {
    if (!this.form.valid) {
      return false;
    }
    const title = this.form.controls.title.value.trim();
    if (!title) {
      return false;
    }
    return this.titleAvailabilityStatus() === 'available';
  }

  private emitState(): void {
    this.validChange.emit(this.isFormReady());
    this.valueChange.emit(this.form.getRawValue() as NgsildBasicInfo);
  }

  showError(
    controlName: 'title' | 'permission' | 'shortDescription' | 'longDescription',
  ): boolean {
    const c = this.form.get(controlName);
    return !!(c && c.invalid && c.touched);
  }

  get shortDescriptionLength(): number {
    return this.form.controls.shortDescription.value.length;
  }

  get longDescriptionLength(): number {
    return this.form.controls.longDescription.value.length;
  }
}
